import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react';
import { useCallback, useMemo, useState } from 'react';

import { useCloseOnOutsideOrEscape } from '@shared/lib/hooks';

export const useHeaderNavigationFloating = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

	const { refs, floatingStyles } = useFloating({
		placement: 'bottom-end',
		strategy: 'fixed',
		open: isOpen,
		onOpenChange: (val) => {
			setIsOpen(val);
			if (!val) setIsFlyoutOpen(false);
		},
		middleware: [offset({ mainAxis: 12, crossAxis: 16 }), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	const flyout = useFloating({
		placement: 'left-start',
		strategy: 'fixed',
		open: isFlyoutOpen,
		onOpenChange: setIsFlyoutOpen,
		middleware: [
			flip({ fallbackPlacements: ['right-start'] }),
			offset(14),
			shift({ padding: 8 }),
			size({
				padding: 8,
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.min(availableHeight, 240)}px`;
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const closeAll = useCallback(() => {
		setIsOpen(false);
		setIsFlyoutOpen(false);
	}, []);

	const toggleMenu = useCallback(() => {
		setIsOpen((prev) => {
			if (prev) setIsFlyoutOpen(false);
			return !prev;
		});
	}, []);

	const handleFlyoutToggle = useCallback(() => {
		setIsFlyoutOpen((prev) => !prev);
	}, []);

	// Include flyout ref only when it is mounted — null ref in useClickOutside treats element
	// as "inside", which would prevent outside-click from closing the main panel.
	const dismissRefs = useMemo(
		() =>
			isFlyoutOpen
				? [refs.reference, refs.floating, flyout.refs.floating]
				: [refs.reference, refs.floating],
		[isFlyoutOpen, refs.reference, refs.floating, flyout.refs.floating],
	);

	// Exclude the language sub-panel portal from outside-click detection:
	// it lives in document.body (portal), so its DOM parent is NOT inside refs.floating,
	// but we don't want a click on a language item to close the whole nav dropdown
	// before the button's onClick fires.
	useCloseOnOutsideOrEscape(dismissRefs, closeAll, isOpen, {
		excludeSelectors: ['[data-language-panel]'],
	});

	return {
		refs,
		floatingStyles,
		flyout,
		isOpen,
		isFlyoutOpen,
		closeAll,
		toggleMenu,
		handleFlyoutToggle,
	};
};
